import React, { useState } from 'react';
import { ArrowUpDown, BadgeCheck, Star } from 'lucide-react';
import { useT } from '../services/i18n';
import { sortByEnergy } from '../services/fuel';

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'cat.sortRelevance' },
  { value: 'price_asc', label: 'cat.sortPriceAsc' },
  { value: 'price_desc', label: 'cat.sortPriceDesc' },
  { value: 'rating', label: 'cat.sortRating' },
  { value: 'discount', label: 'cat.sortDiscount' },
  { value: 'newest', label: 'cat.sortNewest' },
];

export const NEW_DAYS = 7;
export const isRecentProduct = (p) => !!p.created_at && (Date.now() - new Date(p.created_at).getTime()) < NEW_DAYS * 86400000;

export const sortProducts = (products, sort, energy = null) => {
  const list = [...products];
  switch (sort) {
    case 'price_asc': return list.sort((a, b) => a.price - b.price);
    case 'price_desc': return list.sort((a, b) => b.price - a.price);
    case 'rating': return list.sort((a, b) => b.rating - a.rating || b.reviews - a.reviews);
    case 'discount': return list.sort((a, b) => (b.discount || 0) - (a.discount || 0));
    case 'newest': return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    default: return sortByEnergy(list, energy);
  }
};

const SortSelect = ({ value, onChange, count, bestPrice, topRated }) => {
  const { t } = useT();
  return (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="sort-bar">
    <p className="text-sm text-gray-500" data-testid="sort-count">{t('cat.found', { n: count, count })}</p>
    <div className="flex items-center gap-4">
      {bestPrice && bestPrice.total > 0 && (
        <button
          type="button"
          data-testid="best-price-filter"
          aria-pressed={bestPrice.enabled}
          onClick={bestPrice.onToggle}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${bestPrice.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
          title={t('cat.bestPriceTitle')}
        >
          <BadgeCheck size={14} /> {t('common.bestPrice')} <span className={bestPrice.enabled ? 'text-white/80' : 'text-emerald-600/70'}>({bestPrice.total})</span>
        </button>
      )}
      {topRated && topRated.total > 0 && (
        <button
          type="button"
          data-testid="top-rated-filter"
          aria-pressed={topRated.enabled}
          onClick={topRated.onToggle}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${topRated.enabled ? 'bg-yellow-400 text-gray-900 border-yellow-400' : 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100'}`}
          title={t('cat.topRatedTitle')}
        >
          <Star size={14} className={topRated.enabled ? 'fill-gray-900' : 'fill-yellow-400 text-yellow-500'} /> {t('cat.topRated')} <span className={topRated.enabled ? 'text-gray-700' : 'text-yellow-700/70'}>({topRated.total})</span>
        </button>
      )}
    <label className="flex items-center gap-2 text-sm text-gray-600">
      <ArrowUpDown size={16} className="text-gray-400" />
      <span className="hidden sm:inline">{t('cat.sortBy')}</span>
      <select
        data-testid="sort-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
      </select>
    </label>
    </div>
  </div>
  );
};

export const isBestPrice = (p) => !!p.best_price?.active;
export const isTopRated4 = (p) => Number(p.reviews) > 0 && Number(p.rating) >= 4;

/** « 4 ★ et plus » filter state for a listing: returns the filtered list + the props for <SortSelect topRated=…>. */
const TOP_RATED_KEY = 'otopieces.topRatedFilter';
const readTopRated = () => { try { return sessionStorage.getItem(TOP_RATED_KEY) === '1'; } catch { return false; } };

export const useTopRatedFilter = (list) => {
  const [enabled, setEnabled] = useState(readTopRated);
  const total = list.filter(isTopRated4).length;
  const onToggle = () => setEnabled(v => { const next = !v; try { sessionStorage.setItem(TOP_RATED_KEY, next ? '1' : '0'); } catch { /* private mode */ } return next; });
  return { list: enabled ? list.filter(isTopRated4) : list, topRated: { total, enabled, onToggle } };
};

export default SortSelect;
